import React, { useState, useEffect, useMemo } from 'react'
import TournamentForm from '../components/TournamentForm'
import ConfirmDialog from '../components/ConfirmDialog'
import BulkPlayerRegistrationForm from '../components/BulkPlayerRegistrationForm'
import { Tournament, Player, PlayerMembership, Club } from '../types'
import { formatDate } from '../utils/dateFormat'
import { formatMoldovaTime, MOLDOVA_TIMEZONE } from '../utils/timezone'
import TournamentBudgetModal from '../components/TournamentBudgetModal'
import TournamentBudgetBreakdownModal from '../components/TournamentBudgetBreakdownModal'
import TournamentBudgetComparisonModal from '../components/TournamentBudgetComparisonModal'
import TournamentLevelFormatsModal from '../components/TournamentLevelFormatsModal'
import PlayerCancellationModal from '../components/PlayerCancellationModal'
import PlayerDiscountModal from '../components/PlayerDiscountModal'
import TournamentCancellationsListModal from '../components/TournamentCancellationsListModal'
import TournamentDiscountsListModal from '../components/TournamentDiscountsListModal'
import TournamentPointsAccrualModal from '../components/TournamentPointsAccrualModal'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

interface DigestPreviewResponse {
  messageText: string;
  hasChanges: boolean;
  wouldSkip: boolean;
  state: {
    totalRegistered: number;
    levelCounts: Record<string, number>;
    newPlayers: Array<{
      playerId: string;
      displayName: string;
      levelCode: string;
      registeredAt: string;
    }>;
  };
}

interface DigestLogItem {
  id: string;
  status: string;
  triggerType: string;
  errorText?: string | null;
  messageText?: string | null;
  createdAt: string;
}

interface TournamentPointsAccrualItem {
  id: string;
  level: string;
  stageCode?: string | null;
  stageName?: string | null;
  isWinner: boolean;
  winPoints: number;
  stagePoints: number;
  awardedPoints: number;
  player: {
    id: string;
    name: string;
  };
}

interface TournamentPointsAccrualDocument {
  id: string;
  tournamentId: string;
  status: string;
  accrualDate?: string | null;
  itemsCount: number;
  totalPoints: number;
  notes?: string | null;
  createdAt: string;
  tournament?: {
    id: string;
    name: string;
    status: string;
  };
  items: TournamentPointsAccrualItem[];
}

// Раздел «Отмены»: подгружает список и группирует по уровню, изменяет заголовок
function CancellationsSection({ tournament, onOpenCancel, onOpenLevel, disabled = false }: { tournament: any; onOpenCancel: () => void; onOpenLevel: (level: string) => void; disabled?: boolean }) {
  const [count, setCount] = React.useState<number | null>(null)
  const [byLevel, setByLevel] = React.useState<Record<string, number>>({})

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        // используем отчёт взаиморасчетов деталей для получения уровней отмен
        // сначала найдём игроков турнира и пройдёмся — но у нас ID игроков не здесь.
        // Получаем отмены напрямую и группируем по level
        const list = await apiGet(`/player-cancellations?tournamentId=${encodeURIComponent(tournament.id)}`)
        const arr = Array.isArray(list) ? list : (Array.isArray(list?.items) ? list.items : [])
        const grouped: Record<string, number> = {}
        for (const c of arr) {
          const lvl = (c?.level || '').toString().trim()
          if (!lvl) continue
          grouped[lvl] = (grouped[lvl] || 0) + 1
        }
        const total = arr.length || Object.values(grouped).reduce((a,b)=>a+b,0)
        if (!cancelled) {
          setCount(total)
          setByLevel(grouped)
        }
      } catch {
        if (!cancelled) { setCount(0); setByLevel({}) }
      }
    }
    load()
    return () => { cancelled = true }
  }, [tournament?.id])

  if (!count) return null

  return (
    <div className="mt-2">
      <h4 className="text-xs font-medium text-gray-700 mb-1">
        <button
          onClick={onOpenCancel}
          disabled={disabled}
          className={`text-orange-600 hover:text-orange-800 ${disabled ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:text-orange-600' : ''}`}
        >
          Отмены: {count}
        </button>
      </h4>
      {Object.keys(byLevel).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {Object.entries(byLevel).map(([lvl, cnt]) => (
            <button
              key={lvl}
              onClick={() => onOpenLevel(lvl)}
              disabled={disabled}
              className={`px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 ${disabled ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-orange-100' : ''}`}
            >
              {lvl}: {cnt}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Раздел «Коррекции взноса»: подгружает список и группирует по уровню
function FeeCorrectionsSection({ tournament, onOpenDiscount, onOpenLevel, disabled = false }: { tournament: any; onOpenDiscount: () => void; onOpenLevel: (level: string) => void; disabled?: boolean }) {
  const [count, setCount] = React.useState<number | null>(null)
  const [byLevel, setByLevel] = React.useState<Record<string, number>>({})

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        // Получаем корректировки напрямую и группируем по level
        const list = await apiGet(`/player-fee-corrections?tournamentId=${encodeURIComponent(tournament.id)}`)
        const arr = Array.isArray(list) ? list : (Array.isArray(list?.items) ? list.items : [])
        const grouped: Record<string, number> = {}
        for (const c of arr) {
          const lvl = (c?.level || '').toString().trim()
          if (!lvl) continue
          grouped[lvl] = (grouped[lvl] || 0) + 1
        }
        const total = arr.length || Object.values(grouped).reduce((a,b)=>a+b,0)
        if (!cancelled) {
          setCount(total)
          setByLevel(grouped)
        }
      } catch {
        if (!cancelled) { setCount(0); setByLevel({}) }
      }
    }
    load()
    return () => { cancelled = true }
  }, [tournament?.id])

  if (!count) return null

  return (
    <div className="mt-2">
      <h4 className="text-xs font-medium text-gray-700 mb-1">
        <button
          onClick={onOpenDiscount}
          disabled={disabled}
          className={`text-amber-600 hover:text-amber-800 ${disabled ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:text-amber-600' : ''}`}
        >
          Коррекции: {count}
        </button>
      </h4>
      {Object.keys(byLevel).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {Object.entries(byLevel).map(([lvl, cnt]) => (
            <button
              key={lvl}
              onClick={() => onOpenLevel(lvl)}
              disabled={disabled}
              className={`px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 ${disabled ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-amber-100' : ''}`}
            >
              {lvl}: {cnt}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const Tournaments: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  const [players, setPlayers] = useState<Player[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('tournaments-status-filter')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [tournamentToDelete, setTournamentToDelete] = useState<Tournament | null>(null)
  const [showBulkRegistrationForm, setShowBulkRegistrationForm] = useState(false)
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null)
  const [budgetFor, setBudgetFor] = useState<Tournament | null>(null)
  const [formatsFor, setFormatsFor] = useState<Tournament | null>(null)
  const [breakdownFor, setBreakdownFor] = useState<Tournament | null>(null)
  const [comparisonFor, setComparisonFor] = useState<Tournament | null>(null)
  const [cancelFor, setCancelFor] = useState<Tournament | null>(null)
  const [discountFor, setDiscountFor] = useState<Tournament | null>(null)
  const [editingCancellationId, setEditingCancellationId] = useState<string>('')
  const [prefillPlayerId, setPrefillPlayerId] = useState<string>('')
  const [cancellationsLevelFor, setCancellationsLevelFor] = useState<{ tournament: Tournament; level: string } | null>(null)
  const [discountsLevelFor, setDiscountsLevelFor] = useState<{ tournament: Tournament; level: string } | null>(null)
  const [digestPreviewFor, setDigestPreviewFor] = useState<Tournament | null>(null)
  const [digestPreview, setDigestPreview] = useState<DigestPreviewResponse | null>(null)
  const [digestPreviewLoading, setDigestPreviewLoading] = useState(false)
  const [digestSendFor, setDigestSendFor] = useState<Tournament | null>(null)
  const [digestForceSend, setDigestForceSend] = useState(false)
  const [digestSending, setDigestSending] = useState(false)
  const [digestLogsFor, setDigestLogsFor] = useState<Tournament | null>(null)
  const [digestLogs, setDigestLogs] = useState<DigestLogItem[]>([])
  const [digestLogsLoading, setDigestLogsLoading] = useState(false)
  const [pointsAccrualDoc, setPointsAccrualDoc] = useState<TournamentPointsAccrualDocument | null>(null)
  const [pointsAccrualDocByTournament, setPointsAccrualDocByTournament] = useState<Record<string, boolean>>({})
  const [pointsAccrualRecalculationDirty, setPointsAccrualRecalculationDirty] = useState(false)
  const [pointsAccrualRecalculating, setPointsAccrualRecalculating] = useState(false)
  const [pointsAccrualSavingRecalculation, setPointsAccrualSavingRecalculation] = useState(false)
  const [pointsAccrualDateSaving, setPointsAccrualDateSaving] = useState(false)
  // перехват глобального события открытия редактирования ADJUSTMENT из модалки списка
  React.useEffect(() => {
    const handler = (e: any) => {
      const idFromEvent: string = e?.detail?.tournamentId || ''
      const target = (Array.isArray(tournaments) ? tournaments.find((t:any)=> t.id===idFromEvent) : undefined)
        || selectedTournament
        || cancellationsLevelFor?.tournament
        || null
      if (target) {
        setEditingCancellationId(e?.detail?.id || '')
        setPrefillPlayerId(e?.detail?.playerId || '')
        setCancelFor(target as any)
      }
    }
    window.addEventListener('open-adjustment-edit', handler as any)
    return () => window.removeEventListener('open-adjustment-edit', handler as any)
  }, [tournaments, selectedTournament, cancellationsLevelFor])

  // перехват глобального события открытия редактирования коррекции взноса из модалки списка
  React.useEffect(() => {
    const handler = (e: any) => {
      const idFromEvent: string = e?.detail?.tournamentId || ''
      const target = (Array.isArray(tournaments) ? tournaments.find((t:any)=> t.id===idFromEvent) : undefined)
        || selectedTournament
        || discountsLevelFor?.tournament
        || null
      if (target) {
        setEditingCancellationId(e?.detail?.id || '')
        setPrefillPlayerId(e?.detail?.playerId || '')
        setDiscountFor(target as any)
      }
    }
    window.addEventListener('open-fee-correction-edit', handler as any)
    return () => window.removeEventListener('open-fee-correction-edit', handler as any)
  }, [tournaments, selectedTournament, discountsLevelFor])

  useEffect(() => {
    fetchTournaments()
    fetchPlayers()
    fetchPointsAccrualDocumentIndex()
  }, [])

  // Сохранение фильтра статуса в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('tournaments-status-filter', JSON.stringify(statusFilter))
  }, [statusFilter])

  // Закрытие выпадающего списка статусов при нажатии ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && statusDropdownOpen) {
        setStatusDropdownOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [statusDropdownOpen])
  // Получить количество отмен по турниру
  const fetchCancellationsCount = async (tournamentId: string): Promise<number> => {
    try {
      const data = await apiGet<{ count?: number }>(`/player-cancellations?tournamentId=${encodeURIComponent(tournamentId)}`)
      return Number(data?.count || 0)
    } catch { return 0 }
  }


  const fetchTournaments = async () => {
    try {
      const data = await apiGet<{ tournaments: Tournament[] }>('/tournaments?limit=1000')
      // API возвращает объект с полем tournaments
      setTournaments(Array.isArray(data.tournaments) ? data.tournaments : [])
    } catch (error) {
      console.error('Error fetching tournaments:', error)
      setTournaments([])
    } finally {
      setLoading(false)
    }
  }

  const fetchPlayers = async () => {
    try {
      // Загружаем ВСЕ игроков, а не только первую страницу
      const data = await apiGet<{ players: Player[] }>('/players?isActive=true&limit=1000')
      setPlayers(data.players || [])
    } catch (error) {
      console.error('Error fetching players:', error)
    }
  }

  const fetchPointsAccrualDocumentIndex = async () => {
    try {
      const data = await apiGet<{ documents: Array<{ tournamentId: string }> }>('/points-accrual-documents?limit=1000')
      const index: Record<string, boolean> = {}
      const docs = Array.isArray(data?.documents) ? data.documents : []
      docs.forEach((doc) => {
        if (doc?.tournamentId) index[doc.tournamentId] = true
      })
      setPointsAccrualDocByTournament(index)
    } catch (error) {
      console.error('Error fetching points accrual documents index:', error)
      setPointsAccrualDocByTournament({})
    }
  }

  const formatRegistrationDateWithWeekday = (value: string): string => {
    const weekdayRaw = new Intl.DateTimeFormat('ru-RU', {
      weekday: 'short',
      timeZone: MOLDOVA_TIMEZONE
    }).format(new Date(value));
    const weekday = weekdayRaw.replace('.', '').slice(0, 2).toLowerCase();
    const dateTime = formatMoldovaTime(value, 'dd.MM.yyyy HH:mm');
    return `${weekday} ${dateTime}`;
  }

  const getRegistrationDisplay = (tournament: Tournament): { start: string; end: string } | null => {
    const start = tournament.registrationStartAt ? formatRegistrationDateWithWeekday(tournament.registrationStartAt) : ''
    const end = tournament.registrationEndAt ? formatRegistrationDateWithWeekday(tournament.registrationEndAt) : ''
    if (!start && !end) return null
    return {
      start: start || 'не задано',
      end: end || 'не задано'
    }
  }

  const getTournamentPeriod = (tournament: Tournament): string => {
    const start = tournament.startDate ? formatDate(tournament.startDate) : ''
    const end = tournament.endDate ? formatDate(tournament.endDate) : ''
    if (start && end) return `${start} - ${end}`
    return start || end || 'не задан'
  }

  const getRegistrationStatusBadge = (tournament: Tournament): { label: string; className: string } | null => {
    const hasStart = Boolean(tournament.registrationStartAt)
    const hasEnd = Boolean(tournament.registrationEndAt)
    if (!hasStart && !hasEnd) return null

    const now = new Date()
    const start = tournament.registrationStartAt ? new Date(tournament.registrationStartAt) : null
    const end = tournament.registrationEndAt ? new Date(tournament.registrationEndAt) : null

    if (start && Number.isNaN(start.getTime())) return null
    if (end && Number.isNaN(end.getTime())) return null

    if (start && now < start) {
      return { label: 'Не началась', className: 'bg-amber-100 text-amber-800' }
    }
    if (end && now > end) {
      return { label: 'Закрыта', className: 'bg-gray-100 text-gray-700' }
    }
    return { label: 'Открыта', className: 'bg-emerald-100 text-emerald-800' }
  }

  const getTournamentSurfaceBadge = (tournament: Tournament): { label: string; className: string } | null => {
    const clubs = ((tournament as any).tournamentClubs || []) as Array<{ isPrimary?: boolean; surfaceType?: string | null }>;
    const preferredClub = clubs.find((tc) => tc?.isPrimary) || clubs[0];
    const rawSurface =
      (preferredClub?.surfaceType || clubs.find((tc) => String(tc?.surfaceType || '').trim())?.surfaceType || '')
        .toString()
        .trim()
        .toLowerCase();

    if (!rawSurface) return null;

    if (rawSurface === 'clay') {
      return { label: 'Clay', className: 'bg-orange-100 text-orange-700' };
    }
    if (rawSurface === 'hard') {
      return { label: 'Hard', className: 'bg-blue-100 text-blue-700' };
    }

    return { label: rawSurface.charAt(0).toUpperCase() + rawSurface.slice(1), className: 'bg-gray-100 text-gray-700' };
  }

  // Функция для подсчета игроков/команд по уровням
  const getPlayerCountByLevel = (memberships: any[], tournamentFormat?: string) => {
    const activeMemberships = (memberships || []).filter((membership: any) =>
      membership?.status === 'REGISTERED' || membership?.status === 'CONFIRMED'
    )
    const countByLevel: Record<string, number> = {}
    
    // Для парных турниров считаем команды (пары), а не отдельных игроков
    if (tournamentFormat === 'DOUBLES') {
      const teamsByLevel = new Map<string, Set<string>>()
      const soloPlayersByLevel = new Map<string, Set<string>>()
      
      activeMemberships.forEach(membership => {
        const level = membership.level
        if (level) {
          if (!teamsByLevel.has(level)) {
            teamsByLevel.set(level, new Set())
            soloPlayersByLevel.set(level, new Set())
          }
          
          // Если у membership есть doublesTeamId, это команда
          if (membership.doublesTeamId) {
            teamsByLevel.get(level)!.add(membership.doublesTeamId)
          } else {
            // Если нет doublesTeamId, это игрок без пары - считаем как отдельную "команду" из одного игрока
            soloPlayersByLevel.get(level)!.add(membership.playerId)
          }
        }
      })
      
      // Подсчитываем количество уникальных команд для каждого уровня
      // Команды (пары) + игроки без пары
      teamsByLevel.forEach((teams, level) => {
        const soloCount = soloPlayersByLevel.get(level)?.size || 0
        countByLevel[level] = teams.size + soloCount
      })
      
      // Обрабатываем уровни, где есть только игроки без пары
      soloPlayersByLevel.forEach((soloPlayers, level) => {
        if (!countByLevel[level]) {
          countByLevel[level] = soloPlayers.size
        }
      })
    } else {
      // Для одиночных турниров считаем отдельных игроков
      activeMemberships.forEach(membership => {
        const level = membership.level
        if (level) {
          countByLevel[level] = (countByLevel[level] || 0) + 1
        }
      })
    }
    
    return countByLevel
  }

  const parseLevelBrackets = (raw?: string | null): Record<string, any> => {
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }

  const getLevelMaxParticipants = (levelData: any): number | null => {
    if (!levelData) return null

    if (typeof levelData === 'number' && Number.isFinite(levelData)) {
      return Math.floor(levelData)
    }

    if (typeof levelData === 'object') {
      const candidates = [
        levelData.registrationMaxPlayers,
        levelData.max,
        levelData.capacity,
        levelData.slots,
        levelData.limit,
        levelData.total_available,
        levelData.available_total,
        levelData.levelMaxParticipants,
        levelData.level_max_participants,
        levelData.levelCapacity
      ]
      for (const candidate of candidates) {
        if (typeof candidate === 'number' && Number.isFinite(candidate)) {
          return Math.floor(candidate)
        }
        if (typeof candidate === 'string' && candidate.trim() !== '') {
          const parsed = Number(candidate)
          if (Number.isFinite(parsed)) return Math.floor(parsed)
        }
      }
    }

    return null
  }

  const sortLevels = (a: string, b: string) => {
    const parseLevel = (level: string) => {
      const [familyRaw, rankRaw] = level.split('-')
      const family = familyRaw || 'Z'
      let rank = Number.MAX_SAFE_INTEGER
      let sub = 0

      if (rankRaw) {
        if (rankRaw === 'ProAm') {
          rank = 100
        } else if (rankRaw === 'Mixt') {
          rank = 200
        } else {
          const numeric = Number(rankRaw)
          if (Number.isFinite(numeric)) {
            rank = numeric
            if (rankRaw.includes('.')) sub = 1
          }
        }
      }

      return { family, rank, sub, raw: level }
    }

    const pa = parseLevel(a)
    const pb = parseLevel(b)

    if (pa.family !== pb.family) return pa.family.localeCompare(pb.family)
    if (pa.rank !== pb.rank) return pa.rank - pb.rank
    if (pa.sub !== pb.sub) return pa.sub - pb.sub
    return pa.raw.localeCompare(pb.raw)
  }

  const filteredTournaments = (Array.isArray(tournaments) ? tournaments : []).filter(tournament => {
    const matchesSearch = tournament.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         tournament.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(tournament.status)
    return matchesSearch && matchesStatus
  }).sort((a, b) => {
    // Сортировка по дате начала: позже - выше
    const dateA = new Date(a.startDate)
    const dateB = new Date(b.startDate)
    return dateB.getTime() - dateA.getTime()
  })

  const tournamentStatusSections = useMemo(() => {
    const orderedStatuses = ['ACTIVE', 'PLANNED', 'COMPLETED', 'CANCELLED']
    const labelByStatus: Record<string, string> = {
      ACTIVE: 'Активные',
      PLANNED: 'Запланированные',
      COMPLETED: 'Завершенные',
      CANCELLED: 'Отмененные'
    }

    const grouped = orderedStatuses
      .map((status) => ({
        status,
        label: labelByStatus[status],
        items: filteredTournaments.filter((tournament) => tournament.status === status)
      }))
      .filter((section) => section.items.length > 0)

    const others = filteredTournaments.filter((tournament) => !orderedStatuses.includes(String(tournament.status || '').toUpperCase()))
    if (others.length > 0) {
      grouped.push({ status: 'OTHER', label: 'Прочие', items: others })
    }

    return grouped
  }, [filteredTournaments])

  const handleCreate = () => {
    setEditingTournament(null)
    setShowForm(true)
  }

  const handleEdit = (tournament: Tournament) => {
    setEditingTournament(tournament)
    setShowForm(true)
  }

  const handleFormSave = async (tournamentData: Partial<Tournament>) => {
    try {
      if (editingTournament) {
        await apiPut(`/tournaments/${editingTournament.id}`, tournamentData)
      } else {
        await apiPost('/tournaments', tournamentData)
      }
      console.log('Tournament saved successfully')
      setShowForm(false)
      setEditingTournament(null)
      fetchTournaments()
    } catch (error: any) {
      console.error('Error saving tournament:', error)
      alert(`Ошибка: ${error.message || 'Неизвестная ошибка'}`)
      // Принудительно обновляем список турниров после ошибки
      fetchTournaments()
    }
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setEditingTournament(null)
  }

  const handleCreatePointsAccrualDocument = async (tournamentId: string) => {
    try {
      const data = await apiPost<{ created: boolean; document: TournamentPointsAccrualDocument }>(
        `/tournaments/${tournamentId}/points-accrual-document`
      );
      setPointsAccrualDoc(data.document);
      setPointsAccrualRecalculationDirty(false)
      setPointsAccrualDocByTournament((prev) => ({ ...prev, [tournamentId]: true }))
    } catch (error: any) {
      console.error('Ошибка создания документа начисления очков:', error);
      alert(`Ошибка: ${error.message || 'Не удалось создать документ начисления очков'}`);
    }
  };

  const handlePreviewDigest = async (tournament: Tournament) => {
    setDigestPreviewFor(tournament)
    setDigestPreview(null)
    setDigestPreviewLoading(true)
    try {
      const preview = await apiPost<DigestPreviewResponse>(`/registration-digest/tournaments/${tournament.id}/preview`)
      setDigestPreview(preview)
    } catch (error: any) {
      console.error('Error preview digest:', error)
      alert(`Ошибка предпросмотра: ${error.message || 'Неизвестная ошибка'}`)
      setDigestPreviewFor(null)
    } finally {
      setDigestPreviewLoading(false)
    }
  }

  const handlePointsAccrualRecalculatePreview = async () => {
    if (!pointsAccrualDoc?.id) return
    try {
      setPointsAccrualRecalculating(true)
      const preview = await apiPost<TournamentPointsAccrualDocument>(`/points-accrual-documents/${pointsAccrualDoc.id}/recalculate-preview`)
      setPointsAccrualDoc(preview)
      setPointsAccrualRecalculationDirty(true)
    } catch (error: any) {
      console.error('Ошибка пересчета документа начисления очков:', error)
      alert(`Ошибка: ${error.message || 'Не удалось выполнить пересчет'}`)
    } finally {
      setPointsAccrualRecalculating(false)
    }
  }

  const handlePointsAccrualSaveRecalculation = async () => {
    if (!pointsAccrualDoc?.id) return
    try {
      setPointsAccrualSavingRecalculation(true)
      const saved = await apiPost<TournamentPointsAccrualDocument>(`/points-accrual-documents/${pointsAccrualDoc.id}/recalculate-apply`)
      setPointsAccrualDoc(saved)
      setPointsAccrualRecalculationDirty(false)
      setPointsAccrualDocByTournament((prev) => ({ ...prev, [saved.tournamentId]: true }))
    } catch (error: any) {
      console.error('Ошибка сохранения пересчета документа начисления очков:', error)
      alert(`Ошибка: ${error.message || 'Не удалось сохранить пересчет'}`)
    } finally {
      setPointsAccrualSavingRecalculation(false)
    }
  }

  const handlePointsAccrualChangeDate = async (nextDate: string) => {
    if (!pointsAccrualDoc?.id) return
    try {
      setPointsAccrualDateSaving(true)
      const updated = await apiPut<TournamentPointsAccrualDocument>(`/points-accrual-documents/${pointsAccrualDoc.id}/accrual-date`, {
        accrualDate: nextDate
      })
      setPointsAccrualDoc(updated)
    } catch (error: any) {
      console.error('Ошибка обновления даты начисления документа:', error)
      alert(`Ошибка: ${error.message || 'Не удалось обновить дату начисления'}`)
    } finally {
      setPointsAccrualDateSaving(false)
    }
  }

  const handleOpenSendDigest = (tournament: Tournament) => {
    setDigestSendFor(tournament)
    setDigestForceSend(false)
  }

  const handleSendDigestNow = async () => {
    if (!digestSendFor) return
    setDigestSending(true)
    try {
      const result = await apiPost<any>(`/registration-digest/tournaments/${digestSendFor.id}/send-now`, {
        forceSend: digestForceSend
      })
      alert(`Результат отправки: ${result.status}${result.reason ? ` (${result.reason})` : ''}`)
      setDigestSendFor(null)
      setDigestForceSend(false)
      fetchTournaments()
    } catch (error: any) {
      console.error('Error send digest:', error)
      alert(`Ошибка отправки: ${error.message || 'Неизвестная ошибка'}`)
    } finally {
      setDigestSending(false)
    }
  }

  const handleOpenDigestLogs = async (tournament: Tournament) => {
    setDigestLogsFor(tournament)
    setDigestLogs([])
    setDigestLogsLoading(true)
    try {
      const data = await apiGet<{ logs?: DigestLogItem[] }>(`/registration-digest/tournaments/${tournament.id}/logs`)
      setDigestLogs(Array.isArray(data.logs) ? data.logs : [])
    } catch (error: any) {
      console.error('Error loading digest logs:', error)
      alert(`Ошибка загрузки логов: ${error.message || 'Неизвестная ошибка'}`)
      setDigestLogsFor(null)
    } finally {
      setDigestLogsLoading(false)
    }
  }

  const handleDeleteClick = (tournament: Tournament) => {
    setTournamentToDelete(tournament)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!tournamentToDelete) return

    try {
      await apiDelete(`/tournaments/${tournamentToDelete.id}`)
      setShowDeleteConfirm(false)
      setTournamentToDelete(null)
      fetchTournaments()
    } catch (error) {
      console.error('Error deleting tournament:', error)
    }
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
    setTournamentToDelete(null)
  }


  const handleBulkRegisterPlayers = async (tournament: Tournament) => {
    try {
      // Загружаем актуальный список игроков и детали турнира
      const [playersData, tournamentDetails] = await Promise.all([
        apiGet<any>('/players', { isActive: true, limit: 1000 }),
        apiGet<any>(`/tournaments/${tournament.id}`)
      ])
      
      setPlayers(playersData.players || [])
      setSelectedTournament(tournamentDetails)
      setShowBulkRegistrationForm(true)
    } catch (error) {
      console.error('Error fetching data:', error)
      setSelectedTournament(tournament)
      setShowBulkRegistrationForm(true)
    }
  }

  const handleRegistrationSuccess = async () => {
    // Обновляем список турниров
    await fetchTournaments()
    fetchPlayers() // Обновляем список игроков после регистрации
    // Обновляем данные выбранного турнира, если форма еще открыта
    if (selectedTournament) {
      try {
        const updatedTournament = await apiGet<any>(`/tournaments/${selectedTournament.id}`)
        setSelectedTournament(updatedTournament)
        // Обновляем турнир в списке
        setTournaments(prev => prev.map(t => t.id === updatedTournament.id ? updatedTournament : t))
      } catch (error) {
        console.error('Ошибка обновления данных турнира:', error)
      }
    }
  }


  const handleBulkRegistrationClose = () => {
    setShowBulkRegistrationForm(false)
    setSelectedTournament(null)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-xs text-gray-600">Загрузка...</div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Турниры</h1>
          <button 
            onClick={handleCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Создать турнир
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Поиск
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Название или описание"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Статус
              </label>
              <button
                type="button"
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span className="text-sm text-gray-700">
                  {statusFilter.length === 0 
                    ? 'Все' 
                    : statusFilter.length === 1
                    ? [
                        { value: 'PLANNED', label: 'Запланирован' },
                        { value: 'ACTIVE', label: 'Активный' },
                        { value: 'COMPLETED', label: 'Завершен' },
                        { value: 'CANCELLED', label: 'Отменен' }
                      ].find(s => s.value === statusFilter[0])?.label || 'Выбрано'
                    : `Выбрано: ${statusFilter.length}`
                  }
                </span>
                <svg 
                  className={`w-4 h-4 text-gray-500 transition-transform ${statusDropdownOpen ? 'transform rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {statusDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setStatusDropdownOpen(false)}
                  />
                  <div className="absolute z-20 mt-1 w-full border border-gray-300 rounded-lg bg-white shadow-lg max-h-48 overflow-y-auto">
                    <div className="p-2 space-y-1">
                      <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                        <input
                          type="checkbox"
                          checked={statusFilter.length === 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setStatusFilter([])
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Все</span>
                      </label>
                      {[
                        { value: 'PLANNED', label: 'Запланирован' },
                        { value: 'ACTIVE', label: 'Активный' },
                        { value: 'COMPLETED', label: 'Завершен' },
                        { value: 'CANCELLED', label: 'Отменен' }
                      ].map(status => (
                        <label key={status.value} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                          <input
                            type="checkbox"
                            checked={statusFilter.includes(status.value)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                if (!statusFilter.includes(status.value)) {
                                  setStatusFilter([...statusFilter, status.value])
                                }
                              } else {
                                const newFilter = statusFilter.filter(s => s !== status.value)
                                setStatusFilter(newFilter)
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{status.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tournament List */}
        {tournamentStatusSections.map((section) => (
          <div key={section.status} className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-800">{section.label}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {section.items.map((tournament) => {
                        const isCompleted = tournament.status === 'COMPLETED'
                        const isCancelled = tournament.status === 'CANCELLED'
                        const isLocked = isCompleted || isCancelled
                        const isFeeCorrectionDisabled = isCancelled
                        const registrationDisplay = getRegistrationDisplay(tournament)
                        const registrationStatus = getRegistrationStatusBadge(tournament)
                        const surfaceBadge = getTournamentSurfaceBadge(tournament)
                return (
                        <div key={tournament.id} className="bg-white rounded-lg shadow-md p-6 flex flex-col h-full">
              <h3 className="text-xl font-semibold text-gray-900 mb-1">{tournament.name}</h3>
              <p className="text-gray-600 text-xs mb-2">{tournament.description}</p>
              <div className="flex justify-between items-center text-xs text-gray-700 mb-1">
                <span>Период: {getTournamentPeriod(tournament)}</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  tournament.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                  tournament.status === 'PLANNED' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {tournament.status}
                </span>
              </div>
              {/* Клубы и бюджет в одной строке */}
              <div className="flex justify-between items-center text-xs text-gray-700 mb-1">
                <span>
                  {tournament.tournamentClubs && tournament.tournamentClubs.length > 0 ? (
                    <>
                      Клубы: <span className="font-semibold">
                        {tournament.tournamentClubs.map(tc => tc.club.name).join(', ')}
                      </span>
                      {surfaceBadge && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium ${surfaceBadge.className}`}>
                          {surfaceBadge.label}
                        </span>
                      )}
                    </>
                  ) : tournament.club ? (
                    <>
                      Клуб: <span className="font-semibold">{tournament.club.name}</span>
                      {surfaceBadge && (
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-medium ${surfaceBadge.className}`}>
                          {surfaceBadge.label}
                        </span>
                      )}
                    </>
                  ) : ''}
                </span>
                <div className="flex items-center gap-2">
                  {tournament.status === 'COMPLETED' && (
                    <button
                      onClick={() => handleCreatePointsAccrualDocument(tournament.id)}
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        pointsAccrualDocByTournament[tournament.id]
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                      }`}
                    >
                      {pointsAccrualDocByTournament[tournament.id] ? 'Начисленные очки' : 'Начислить очки'}
                    </button>
                  )}
                  <span className="text-gray-700">
                    Взнос:{' '}
                    <span className="font-semibold">
                      {Number.isFinite(Number((tournament as any).baseFee ?? (tournament as any).base_fee))
                        ? `${Math.round(Number((tournament as any).baseFee ?? (tournament as any).base_fee)).toLocaleString('ru-RU')} MDL`
                        : '-'}
                    </span>
                  </span>
                </div>
              </div>
              {registrationDisplay && (
                <div className="text-xs text-gray-700 mb-1 text-left">
                  <div className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-0.5 items-center">
                    <span>Регистрация:</span>
                    <span>{registrationDisplay.start}</span>
                    <span>
                      {registrationStatus && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${registrationStatus.className}`}>
                          {registrationStatus.label}
                        </span>
                      )}
                    </span>
                    <span>{registrationDisplay.end} ({MOLDOVA_TIMEZONE})</span>
                  </div>
                </div>
              )}

              {/* Контент, который растягивается */}
              <div className="flex-grow">
                {tournament.memberships && tournament.memberships.length > 0 && (() => {
                  const activeMemberships = (tournament.memberships || []).filter(
                    (membership: any) => membership?.status === 'REGISTERED' || membership?.status === 'CONFIRMED'
                  );
                  const countByLevel = getPlayerCountByLevel(activeMemberships, tournament.format);
                  const totalCount = tournament.format === 'DOUBLES' 
                    ? Object.values(countByLevel).reduce((sum, count) => sum + count, 0)
                    : activeMemberships.length;
                  const label = tournament.format === 'DOUBLES' ? 'команд' : 'игроков';
                  
                  // Временная диагностика для турнира D-ProAm
                  if (tournament.name?.includes('D-ProAm') && tournament.format === 'DOUBLES') {
                    const dProAmMemberships = activeMemberships.filter((m: any) => m.level === 'D-ProAm');
                    const dProAmTeams = new Set(dProAmMemberships.map((m: any) => m.doublesTeamId).filter(Boolean));
                    console.log(`[DEBUG] Турнир ${tournament.name}:`, {
                      totalMemberships: activeMemberships.length,
                      dProAmMemberships: dProAmMemberships.length,
                      dProAmTeams: dProAmTeams.size,
                      countByLevel: countByLevel['D-ProAm']
                    });
                  }
                  
                  return (
                    <div className="mt-2">
                      <h4 className="text-xs font-medium text-gray-700 mb-1">Участники ({totalCount} {label}):</h4>
                      
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const levelBrackets = parseLevelBrackets((tournament as any).levelBrackets)
                          return Object.entries(countByLevel)
                            .sort(([levelA], [levelB]) => sortLevels(levelA, levelB))
                            .map(([level, count]) => {
                              const maxPlayers = getLevelMaxParticipants(levelBrackets[level])
                              return (
                                <span
                                  key={level}
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    level.startsWith('S-')
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}
                                >
                                  {level}: {count}{maxPlayers !== null ? `/${maxPlayers}` : ''}
                                </span>
                              )
                            })
                        })()}
                      </div>
                    </div>
                  );
                })()}

              {/* Отмены (динамический заголовок и бейджи по уровням) */}
              <CancellationsSection 
                tournament={tournament}
                onOpenCancel={() => setCancelFor(tournament)}
                onOpenLevel={(level) => setCancellationsLevelFor({ tournament, level })}
                disabled={isCompleted}
              />

              {/* Коррекции взноса (динамический заголовок и бейджи по уровням) */}
              <FeeCorrectionsSection 
                tournament={tournament}
                onOpenDiscount={() => setDiscountFor(tournament)}
                onOpenLevel={(level) => setDiscountsLevelFor({ tournament, level })}
                disabled={isFeeCorrectionDisabled}
              />
              </div>
              
              {/* Кнопки всегда внизу */}
              <div className="mt-3 space-y-1">
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleBulkRegisterPlayers(tournament)}
                    disabled={isLocked}
                    className={`flex-1 bg-blue-600 text-white px-3 py-2 rounded-md text-xs hover:bg-blue-700 ${isLocked ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-blue-600' : ''}`}
                  >
                    Регистрация игроков
                  </button>
                  <button 
                    onClick={() => setCancelFor(tournament)}
                    disabled={isLocked}
                    className={`flex-1 bg-orange-600 text-white px-3 py-2 rounded-md text-xs hover:bg-orange-700 ${isLocked ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-orange-600' : ''}`}
                  >
                    Отмена участия
                  </button>
                  <button 
                    onClick={() => setDiscountFor(tournament)}
                    disabled={isFeeCorrectionDisabled}
                    className={`flex-1 bg-amber-600 text-white px-3 py-2 rounded-md text-xs hover:bg-amber-700 ${isFeeCorrectionDisabled ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-amber-600' : ''}`}
                  >
                    Коррекция взноса
                  </button>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleEdit(tournament)}
                    className="flex-1 bg-gray-200 text-gray-800 px-3 py-2 rounded-md text-xs hover:bg-gray-300"
                  >
                    Изменить
                  </button>
                  <button 
                    onClick={() => setFormatsFor(tournament)}
                    disabled={isLocked}
                    className={`flex-1 bg-purple-600 text-white px-3 py-2 rounded-md text-xs hover:bg-purple-700 ${isLocked ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-purple-600' : ''}`}
                  >
                    Сетки
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(tournament)}
                    disabled={isLocked}
                    className={`flex-1 bg-red-200 text-red-800 px-3 py-2 rounded-md text-xs hover:bg-red-300 ${isLocked ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-red-200' : ''}`}
                  >
                    Удалить
                  </button>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePreviewDigest(tournament)}
                    disabled={isLocked}
                    className={`flex-1 bg-indigo-600 text-white px-3 py-2 rounded-md text-xs hover:bg-indigo-700 ${isLocked ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-indigo-600' : ''}`}
                  >
                    Preview digest
                  </button>
                  <button
                    onClick={() => handleOpenSendDigest(tournament)}
                    disabled={isLocked}
                    className={`flex-1 bg-emerald-600 text-white px-3 py-2 rounded-md text-xs hover:bg-emerald-700 ${isLocked ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-emerald-600' : ''}`}
                  >
                    Отправить сводку
                  </button>
                  <button
                    onClick={() => handleOpenDigestLogs(tournament)}
                    disabled={isLocked}
                    className={`flex-1 bg-slate-600 text-white px-3 py-2 rounded-md text-xs hover:bg-slate-700 ${isLocked ? 'opacity-60 cursor-not-allowed filter brightness-90 saturate-50 hover:bg-slate-600' : ''}`}
                  >
                    Логи digest
                  </button>
                </div>
              </div>
            </div>
                )
              })}
            </div>
          </div>
        ))}
        
        {filteredTournaments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Турниры не найдены
          </div>
        )}
      </div>

      {showForm && (
        <TournamentForm
          tournament={editingTournament || undefined}
          onSave={handleFormSave}
          onCancel={handleFormCancel}
        />
      )}

      <TournamentPointsAccrualModal
        open={!!pointsAccrualDoc}
        onClose={() => {
          setPointsAccrualDoc(null)
          setPointsAccrualRecalculationDirty(false)
        }}
        document={pointsAccrualDoc}
        onRecalculate={pointsAccrualDoc?.status === 'DRAFT' ? handlePointsAccrualRecalculatePreview : undefined}
        onSaveRecalculation={handlePointsAccrualSaveRecalculation}
        showSaveButton={pointsAccrualRecalculationDirty && pointsAccrualDoc?.status === 'DRAFT'}
        recalculateLoading={pointsAccrualRecalculating}
        saveLoading={pointsAccrualSavingRecalculation}
        onChangeAccrualDate={pointsAccrualDoc?.status === 'DRAFT' ? handlePointsAccrualChangeDate : undefined}
        accrualDateSaving={pointsAccrualDateSaving}
      />

      {digestPreviewFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold">
                Предпросмотр digest: {digestPreviewFor.name}
              </h3>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setDigestPreviewFor(null)}
              >
                Закрыть
              </button>
            </div>
            <div className="p-4 space-y-3">
              {digestPreviewLoading && (
                <p className="text-sm text-gray-600">Загрузка предпросмотра...</p>
              )}
              {!digestPreviewLoading && digestPreview && (
                <>
                  <div className="text-sm text-gray-700">
                    <p>Изменения: <b>{digestPreview.hasChanges ? 'есть' : 'нет'}</b></p>
                    <p>При обычной отправке: <b>{digestPreview.wouldSkip ? 'будет skipped' : 'будет отправлено'}</b></p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap text-sm">
                    {digestPreview.messageText}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {digestSendFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold">Отправить сводку сейчас</h3>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-700">
                Турнир: <b>{digestSendFor.name}</b>
              </p>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={digestForceSend}
                  onChange={(e) => setDigestForceSend(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Отправить даже если нет изменений
                </span>
              </label>
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex gap-2">
              <button
                type="button"
                onClick={() => { setDigestSendFor(null); setDigestForceSend(false) }}
                className="flex-1 bg-gray-200 text-gray-800 px-3 py-2 rounded-md text-sm hover:bg-gray-300"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={handleSendDigestNow}
                disabled={digestSending}
                className="flex-1 bg-emerald-600 text-white px-3 py-2 rounded-md text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {digestSending ? 'Отправка...' : 'Отправить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {digestLogsFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold">История digest: {digestLogsFor.name}</h3>
              <button
                type="button"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setDigestLogsFor(null)}
              >
                Закрыть
              </button>
            </div>
            <div className="p-4">
              {digestLogsLoading ? (
                <p className="text-sm text-gray-600">Загрузка логов...</p>
              ) : digestLogs.length === 0 ? (
                <p className="text-sm text-gray-600">Логи отсутствуют.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 border-b">Дата</th>
                        <th className="text-left px-3 py-2 border-b">Trigger</th>
                        <th className="text-left px-3 py-2 border-b">Статус</th>
                        <th className="text-left px-3 py-2 border-b">Ошибка</th>
                        <th className="text-left px-3 py-2 border-b">Preview</th>
                      </tr>
                    </thead>
                    <tbody>
                      {digestLogs.map((log) => (
                        <tr key={log.id} className="odd:bg-white even:bg-gray-50">
                          <td className="px-3 py-2 border-b">{formatDate(log.createdAt)}</td>
                          <td className="px-3 py-2 border-b">{log.triggerType}</td>
                          <td className="px-3 py-2 border-b">{log.status}</td>
                          <td className="px-3 py-2 border-b text-red-700">{log.errorText || '-'}</td>
                          <td className="px-3 py-2 border-b max-w-md truncate" title={log.messageText || ''}>
                            {log.messageText || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          title="Удалить турнир"
          message={`Вы уверены, что хотите удалить турнир "${tournamentToDelete?.name}"? Это действие нельзя отменить.`}
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          type="danger"
        />
      )}

      {showBulkRegistrationForm && selectedTournament && (
        <BulkPlayerRegistrationForm
          tournament={selectedTournament}
          players={players}
          onClose={handleBulkRegistrationClose}
          onSuccess={handleRegistrationSuccess}
        />
      )}

      {budgetFor && (
        <TournamentBudgetModal
          tournament={budgetFor}
          onClose={() => setBudgetFor(null)}
        />
      )}

      {breakdownFor && (
        <TournamentBudgetBreakdownModal
          tournament={breakdownFor}
          onClose={() => setBreakdownFor(null)}
        />
      )}

      {comparisonFor && (
        <TournamentBudgetComparisonModal
          tournament={comparisonFor}
          onClose={() => setComparisonFor(null)}
        />
      )}

      {formatsFor && (
        <TournamentLevelFormatsModal
          tournament={formatsFor}
          onClose={() => {
            setFormatsFor(null);
            fetchTournaments(); // Обновляем список после сохранения
          }}
        />
      )}

      {cancelFor && (
        <PlayerCancellationModal
          tournament={cancelFor}
          players={players}
          isOpen={!!cancelFor}
          editingId={editingCancellationId}
          prefillPlayerId={prefillPlayerId}
          onClose={() => { setCancelFor(null); setEditingCancellationId(''); setPrefillPlayerId('') }}
          onSuccess={() => fetchTournaments()}
        />
      )}

      {discountFor && (
        <PlayerDiscountModal
          tournament={discountFor}
          players={players}
          isOpen={!!discountFor}
          editingId={editingCancellationId}
          prefillPlayerId={prefillPlayerId}
          onClose={() => { setDiscountFor(null); setEditingCancellationId(''); setPrefillPlayerId('') }}
          onSuccess={() => fetchTournaments()}
        />
      )}

      {cancellationsLevelFor && (
        <TournamentCancellationsListModal
          tournament={cancellationsLevelFor.tournament}
          level={cancellationsLevelFor.level}
          open={!!cancellationsLevelFor}
          onClose={() => setCancellationsLevelFor(null)}
        />
      )}

      {discountsLevelFor && (
        <TournamentDiscountsListModal
          tournament={discountsLevelFor.tournament}
          level={discountsLevelFor.level}
          open={!!discountsLevelFor}
          onClose={() => setDiscountsLevelFor(null)}
        />
      )}
    </>
  )
}

// Вспомогательный блок «Бюджет: Расшифровка | Сравнение | сумма из снапшота»
const BudgetBlock: React.FC<{ tournament: any; onOpenBreakdown: ()=>void; onOpenComparison: ()=>void }> = ({ tournament, onOpenBreakdown, onOpenComparison }) => {
  const [sum, setSum] = React.useState<number | null>(null)
  const fmtInt = (n: number) => {
    try { return Math.round(n).toLocaleString('ru-RU') } catch { return Math.round(n).toString() }
  }

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      // пробуем взять последний snapshot из БД
      try {
        const list = await apiGet<any[]>(`/tournaments/${tournament.id}/budget-snapshots`)
        if (Array.isArray(list) && list.length > 0) {
          const payload = JSON.parse(list[0].payload || '{}')
          const snap = payload?.snapshot
          const net = Number(snap?.net ?? 0)
          if (!cancelled) setSum(net)
          return
        }
      } catch {}

      // fallback на localStorage
      try {
        const raw = localStorage.getItem(`tournament-budget-${tournament.id}`)
        if (raw) {
          const data = JSON.parse(raw)
          const net = Number(data?.snapshot?.net ?? 0)
          if (!cancelled) setSum(net)
        }
      } catch {}
    })()
    return () => { cancelled = true }
  }, [tournament?.id])

  return (
    <div className="text-xs text-gray-700">
      <span>Бюджет: </span>
      {sum !== null ? (
        <button 
          onClick={onOpenBreakdown}
          className="text-xs font-semibold text-blue-600 hover:text-blue-800 underline"
        >
          {fmtInt(sum)} MDL
        </button>
      ) : (
        <span className="text-xs font-semibold text-gray-500">...</span>
      )}
    </div>
  )
}

// Внутренний компонент бейджа с количеством отмен
function CancellationsBadge({ tournamentId, onOpen }: { tournamentId: string; onOpen: () => void }) {
  const [count, setCount] = React.useState<number | null>(null)
  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const data = await apiGet<{ count?: number }>(`/player-cancellations?tournamentId=${encodeURIComponent(tournamentId)}`)
        if (!cancelled) setCount(Number(data?.count || 0))
      } catch {
        if (!cancelled) setCount(0)
      }
    }
    load()
    return () => { cancelled = true }
  }, [tournamentId])
  const label = count === null ? '...' : String(count)
  return (
    <button onClick={onOpen} className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200">
      Отмены{count !== null ? `: ${label}` : ''}
    </button>
  )
}

export default Tournaments